import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import {
  Plus, Loader2, Trash2, Pencil, FileSignature, Eye, ToggleLeft, ToggleRight,
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });
};

function TermsDialog({ open, onClose, onSaved, editing }) {
  const api = useApi();
  const [form, setForm] = useState({ title: 'Terms & Conditions', content: '', enabled: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing ? {
        title: editing.title || 'Terms & Conditions',
        content: editing.content || '',
        enabled: !!editing.enabled,
      } : { title: 'Terms & Conditions', content: '', enabled: true });
    }
  }, [open, editing]);

  const save = async () => {
    if (!form.content.trim()) { toast.error('Content is required'); return; }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/seat/terms/${editing.id}`, form);
        toast.success('Terms updated');
      } else {
        await api.post('/seat/terms', form);
        toast.success('Terms created');
      }
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="terms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-indigo-600" />
            {editing?.id ? `Edit Terms (v${editing.version})` : 'New Terms & Conditions'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Terms & Conditions" data-testid="terms-title" />
          </div>
          <div>
            <Label className="text-xs">Content * (plain text — line breaks preserved)</Label>
            <textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={12}
              className="w-full border rounded px-2 py-1.5 text-sm font-mono mt-1"
              placeholder={"1. Booking is only for the registered Misaq Done lady.\n2. Each HOF can book up to N seats per floor.\n3. Cancellation requires admin approval.\n4. ..."}
              data-testid="terms-content"
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              data-testid="terms-enabled"
            />
            <strong>Enabled</strong> — when on, HOFs must accept this before seat booking. Saving with this enabled will disable any other previously-active terms.
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="terms-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SeatTermsPanel() {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/seat/terms');
      setItems(res.data?.terms || []);
    } catch { toast.error('Failed to load terms'); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete terms v${t.version}? Existing acceptances by HOFs will also be cleared.`)) return;
    try {
      await api.delete(`/seat/terms/${t.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const toggleEnabled = async (t) => {
    try {
      await api.put(`/seat/terms/${t.id}`, { enabled: !t.enabled });
      toast.success(`Terms v${t.version} ${!t.enabled ? 'enabled' : 'disabled'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const activeOne = items.find(t => t.enabled);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-indigo-600" /> Terms & Conditions
          </h2>
          <p className="text-xs text-slate-500">
            Configure the Terms shown to HOFs before they book a seat. Only one set of terms can be enabled at a time.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setShowDialog(true); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-terms-btn">
          <Plus className="w-4 h-4 mr-1" /> New Terms
        </Button>
      </div>

      {activeOne && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-3 flex items-center gap-2 text-sm">
            <ToggleRight className="w-5 h-5 text-emerald-700" />
            <div className="flex-1">
              <span className="font-semibold text-emerald-900">Active: </span>
              <span className="text-emerald-800">{activeOne.title}</span>
              <Badge variant="outline" className="ml-2 text-[10px]">v{activeOne.version}</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          No terms configured yet. Click <strong>New Terms</strong> above.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map(t => (
            <Card key={t.id} className={t.enabled ? 'border-emerald-300' : ''} data-testid={`terms-card-${t.id}`}>
              <CardContent className="p-3 flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold truncate">{t.title}</h3>
                    <Badge variant="outline" className="text-[10px]">v{t.version}</Badge>
                    {t.enabled ? (
                      <Badge className="bg-emerald-600 text-[10px]">ACTIVE</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-slate-500">Disabled</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-2 mt-1">{(t.content || '').slice(0, 200)}{(t.content || '').length > 200 ? '…' : ''}</p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    {t.created_at ? new Date(t.created_at).toLocaleString() : ''} · by {t.created_by_name || '—'}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setPreviewing(t)} data-testid={`terms-preview-${t.id}`}>
                    <Eye className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleEnabled(t)} data-testid={`terms-toggle-${t.id}`}
                    title={t.enabled ? 'Disable' : 'Enable'}>
                    {t.enabled ? <ToggleRight className="w-3 h-3 text-emerald-600" /> : <ToggleLeft className="w-3 h-3 text-slate-500" />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(t); setShowDialog(true); }}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(t)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TermsDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onSaved={() => { setShowDialog(false); load(); }}
        editing={editing}
      />

      <Dialog open={!!previewing} onOpenChange={(o) => !o && setPreviewing(null)}>
        <DialogContent className="max-w-2xl" data-testid="terms-preview-dialog">
          <DialogHeader>
            <DialogTitle>{previewing?.title} <Badge variant="outline" className="ml-1 text-[10px]">v{previewing?.version}</Badge></DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto bg-slate-50 p-3 rounded border whitespace-pre-wrap text-sm">
            {previewing?.content || ''}
          </div>
          <DialogFooter>
            <Button onClick={() => setPreviewing(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
